import { useNavigate } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { CATEGORY_CONFIG, fmtDateTime } from "@/lib/constants";
import { MapPin, User, Truck, Clock, ChevronRight, Hash, ShieldCheck } from "lucide-react";

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "—";
}

export default function JobCard({ job, index = 0 }) {
  const navigate = useNavigate();
  const cat = CATEGORY_CONFIG[job.category] || { label: job.category, color: "#48484a", tint: "#eaeaea" };
  const creator = (job.activity_log || []).find((a) => a.action === "created");
  const confirmedBy = creator?.actor_name;
  const techLead = job.assigned_tech_names?.[0];

  return (
    <button
      data-testid={`job-card-${job.id}`}
      onClick={() => navigate(`/jobs/${job.id}`)}
      style={{ animationDelay: `${Math.min(index * 40, 300)}ms`, borderLeft: `4px solid ${cat.color}` }}
      className="stark-card stark-fade-up group w-full overflow-hidden p-0 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-12px_rgba(28,28,30,0.25)]"
    >
      {/* Header band tinted by job type */}
      <div className="flex items-center gap-2 px-5 py-2.5" style={{ backgroundColor: cat.tint }}>
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
          style={{ backgroundColor: cat.color }}
          title={confirmedBy ? `Confirmed by ${confirmedBy}` : "Confirmed by —"}
          data-testid={`job-confirmer-${job.id}`}
        >
          {initials(confirmedBy)}
        </span>
        <span className="rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white" style={{ backgroundColor: cat.color }}>
          {cat.label}
        </span>
        {job.po_number && (
          <span className="flex items-center gap-0.5 text-[11px] font-bold text-stark-muted">
            <Hash className="h-3 w-3" />{job.po_number}
          </span>
        )}
        {techLead && (
          <span className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-stark-ink">
            <ShieldCheck className="h-3.5 w-3.5" style={{ color: cat.color }} /> {techLead}
          </span>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold text-stark-ink">{job.customer_name}</h3>
            <div className="mt-1 flex items-center gap-1.5 text-sm text-stark-muted">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{job.address}</span>
            </div>
          </div>
          <StatusBadge status={job.status} size="sm" />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-stark-border pt-3 text-xs font-medium text-stark-muted">
          <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {fmtDateTime(job.scheduled_at)}</span>
          {job.assigned_tech_names?.length > 1 && (
            <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> +{job.assigned_tech_names.length - 1} crew</span>
          )}
          {job.truck_name && <span className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> {job.truck_name}</span>}
          <ChevronRight className="ml-auto h-4 w-4 text-stark-border-strong transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </button>
  );
}
